import type { BossExtrudeSceneContour, ProfileRegionGeometry } from '@puppycad/cad-core';
import type { ExtrudeTarget } from '@/composables/extrude-types';
import { computeCenter } from '@/composables/extrude-utils';

/**
 * 拉伸轮廓 UI 抽象。
 *
 * 设计说明：
 * 1. 轮廓场景恢复真相已经在 cad-core 的 `BossExtrudeSceneResolver` 中维护；
 * 2. web-client 只保留标签格式化与拖拽 target 投影；
 * 3. 通过直接复用 cad-core 轮廓类型，避免宿主再维护平行的领域结构。
 */
export type ContourLike = BossExtrudeSceneContour;

/**
 * 解析草图显示名称。
 *
 * @param sketchId 草图 ID。
 * @returns 草图显示名称。
 */
export function resolveSketchDisplayName(sketchId: string): string {
  const match = /(\d+)/.exec(sketchId);
  if (!match) {
    return '草图1';
  }
  return `草图${match[1]}`;
}

/**
 * 解析轮廓对应的草图显示名称。
 *
 * @param contour 轮廓对象。
 * @returns 草图显示名称。
 */
export function resolveSketchDisplayNameFromContour(contour: ContourLike): string {
  const sketchName = contour.sceneObject?.parent?.name?.trim();
  if (sketchName) {
    return sketchName;
  }
  return resolveSketchDisplayName(contour.sketchId);
}

/**
 * 解析轮廓显示标签。
 *
 * @param contour 轮廓。
 * @param siblingContours 同草图轮廓集合。
 * @returns 显示标签。
 */
export function resolveContourLabel(contour: ContourLike, siblingContours: ContourLike[]): string {
  const sketchName = resolveSketchDisplayNameFromContour(contour);
  const contourIndex = Math.max(1, siblingContours.findIndex((item) => item.id === contour.id) + 1);
  return `${sketchName}-局部范围${contourIndex}`;
}

/**
 * 将轮廓集合构造成拉伸目标。
 *
 * @param contours 轮廓集合。
 * @returns 拉伸目标。
 */
export function buildTargetFromContours(contours: ContourLike[]): ExtrudeTarget {
  const contourKeys = contours.map((item) => item.contourKey ?? item.id);
  const regionGeometries = contours.map((item) => item.geometry);
  const contourSnapshots = contours.map((item) => ({
    regionId: item.id,
    geometry: structuredClone(item.geometry),
    sketchId: item.sketchId,
    contourSourceObjectId: item.sourceObjectId,
    contourKey: item.contourKey,
    regionIdentity: item.regionIdentity
      ? structuredClone(item.regionIdentity)
      : undefined,
  }));
  const center = contours.length > 1
    ? computeCenter(contours.flatMap((item) => item.geometry.outer))
    : contours[0]!.basePoint.clone();

  return {
    params: {
      sourceType: 'region_geometry',
      sketchId: contours[0]!.sketchId,
      contourKeys,
      regionGeometry: regionGeometries[0] as ProfileRegionGeometry,
      regionGeometries,
    },
    selection: {
      kind: 'contours',
      contourIds: contours.map((item) => item.id),
      contours: contourSnapshots,
    },
    normal: contours[0]!.normal.clone(),
    basePoint: center,
    regions: contourSnapshots,
  };
}
